import { FC, useMemo, useRef, useState } from "react";
import { Box, Stack, Typography, styled } from "@mui/material";
import { IconChevronDown, IconClose, IconFilterList } from "@/assets/icons";
import { black, button, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { TPagination } from "@/types/api";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import {
  TLearningProgressFilterFormExtended,
  defaultLearningProgressFilterValues,
  isEmptyLearningProgressFilterValue,
} from "@/features/learning-progress/components/filter/learning-progress-filter-shared";
import { LearningProgressFilterModal } from "@/features/learning-progress/components/filter/LearningProgressFilterModal";

type Props = {
  pagination?: TPagination;
  onApplyAdvancedFilters?: (filters: TLearningProgressFilterFormExtended) => void;
  currentAdvancedFilters?: TLearningProgressFilterFormExtended | null;
  rightSlot?: React.ReactNode;
  industryId?: number;
};

export const LearningProgressListTop: FC<Props> = ({
  pagination,
  onApplyAdvancedFilters,
  currentAdvancedFilters,
  rightSlot,
  industryId,
}) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

  const total = pagination?.total ?? 0;
  const page = pagination?.page ?? START_PAGE;
  const perPage = pagination?.perPage ?? DEFAULT_PER_PAGE;

  // Each range (date or numeric, from+to) counts as a single active filter.
  const activeFilterCount = useMemo(() => {
    if (!currentAdvancedFilters) return 0;
    const {
      lastLoginDateFrom,
      lastLoginDateTo,
      learningTimeFrom,
      learningTimeTo,
      thisWeekLearningTimeFrom,
      thisWeekLearningTimeTo,
      lastExamScoreFrom,
      lastExamScoreTo,
      mockExamCountFrom,
      mockExamCountTo,
      latestMockExamScoreFrom,
      latestMockExamScoreTo,
      learnedVocabCountFrom,
      learnedVocabCountTo,
      ...rest
    } = currentAdvancedFilters;
    let count = Object.values(rest).filter((v) => !isEmptyLearningProgressFilterValue(v)).length;
    if (lastLoginDateFrom || lastLoginDateTo) count += 1;
    if (learningTimeFrom || learningTimeTo) count += 1;
    if (thisWeekLearningTimeFrom || thisWeekLearningTimeTo) count += 1;
    if (lastExamScoreFrom || lastExamScoreTo) count += 1;
    if (mockExamCountFrom || mockExamCountTo) count += 1;
    if (latestMockExamScoreFrom || latestMockExamScoreTo) count += 1;
    if (learnedVocabCountFrom || learnedVocabCountTo) count += 1;
    return count;
  }, [currentAdvancedFilters]);

  const openFilterPopover = () => setFilterAnchorEl(anchorRef.current);
  const closeFilterPopover = () => setFilterAnchorEl(null);

  const handleClearAppliedFilter = () => {
    onApplyAdvancedFilters?.({ ...defaultLearningProgressFilterValues });
    setIsClearConfirmOpen(false);
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1.5} ref={anchorRef} gap={1.5}>
          <Typography variant="body14Semi">
            全 {total.toLocaleString()} 件中 {Math.min(page * perPage, total)} 件を表示
          </Typography>
          {activeFilterCount > 0 ? (
            <ActiveFilterButton>
              <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={openFilterPopover}>
                <IconFilterList width={20} height={20} color={black[80]} />
                <Typography variant="cap12Bold" sx={{ mx: 1 }}>
                  フィルタ適用数:{" "}
                  <Typography component="span" variant="cap12Bold" sx={{ color: button.primary }}>
                    {String(activeFilterCount).padStart(2, "0")}
                  </Typography>
                </Typography>
              </Box>
              <Box
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsClearConfirmOpen(true);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  ml: 0.5,
                  "&:hover": { opacity: 0.7 },
                }}
              >
                <IconClose width={16} height={16} color={black[80]} />
              </Box>
            </ActiveFilterButton>
          ) : (
            <FilterButton onClick={openFilterPopover} data-open={Boolean(filterAnchorEl)}>
              <IconFilterList width={20} height={20} color={black[80]} />
              <Typography variant="cap12Bold" sx={{ color: text.primary, mx: 1 }}>
                フィルタ
              </Typography>
              <IconChevronDown
                width={20}
                height={20}
                color={black[80]}
                style={{
                  transition: "transform 0.2s ease",
                  transform: filterAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </FilterButton>
          )}
        </Stack>
        {rightSlot}
      </Stack>

      <LearningProgressFilterModal
        anchorEl={filterAnchorEl}
        onClose={closeFilterPopover}
        onApplyFilters={onApplyAdvancedFilters}
        initialFilters={currentAdvancedFilters ?? undefined}
        industryId={industryId}
      />

      <ConfirmDeleteModal
        open={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onOk={handleClearAppliedFilter}
        message="適用中のフィルタを解除しますがよろしいですか？"
        title=""
      />
    </>
  );
};

const FilterButton = styled(Box)`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid ${divider.middle};
  border-radius: ${rounded.sm};
  cursor: pointer;
  &:hover,
  &[data-open="true"] {
    border-color: ${button.primary};
  }
`;

const ActiveFilterButton = styled(Box)`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid ${button.primary};
  border-radius: ${rounded.sm};
  background: #ecf9f9;
`;
